import { useState, useEffect, useRef } from 'react';
import { searchQuestions, QuestionSearchItem } from '../../api/admin.api';
import { Button } from '../ui/button';
import { Search, X } from 'lucide-react';

interface SurveySearchModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSelect: (questionId: number, description: string) => void;
}

export const SurveySearchModal = ({ isOpen, onClose, onSelect }: SurveySearchModalProps) => {
  const [keyword, setKeyword] = useState('');
  const [results, setResults] = useState<QuestionSearchItem[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const debounceRef = useRef<ReturnType<typeof setTimeout>>(null);

  useEffect(() => {
    if (isOpen) {
      setKeyword('');
      setResults([]);
      setTimeout(() => inputRef.current?.focus(), 100);
    }
  }, [isOpen]);

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);

    if (!keyword.trim()) {
      setResults([]);
      return;
    }

    debounceRef.current = setTimeout(async () => {
      setIsLoading(true);
      try {
        const data = await searchQuestions(keyword.trim(), 20);
        setResults(data);
      } catch {
        setResults([]);
      } finally {
        setIsLoading(false);
      }
    }, 300);

    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [keyword]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-lg mx-4 max-h-[80vh] flex flex-col">
        {/* 헤더 */}
        <div className="flex items-center justify-between p-4 border-b">
          <h3 className="font-semibold text-gray-900">설문 검색</h3>
          <Button variant="ghost" size="sm" onClick={onClose}>
            <X className="w-4 h-4" />
          </Button>
        </div>

        {/* 검색 입력 */}
        <div className="p-4 border-b">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              ref={inputRef}
              type="text"
              value={keyword}
              onChange={(e) => setKeyword(e.target.value)}
              placeholder="설문 제목으로 검색..."
              className="w-full pl-9 pr-3 py-2.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
        </div>

        {/* 결과 리스트 */}
        <div className="flex-1 overflow-y-auto p-2">
          {isLoading && (
            <p className="text-center text-sm text-gray-400 py-8">검색 중...</p>
          )}
          {!isLoading && keyword && results.length === 0 && (
            <p className="text-center text-sm text-gray-400 py-8">검색 결과가 없습니다</p>
          )}
          {results.map((item) => (
            <button
              key={item.questionId}
              onClick={() => {
                onSelect(item.questionId, item.description);
                onClose();
              }}
              className="w-full text-left px-3 py-3 rounded-lg hover:bg-gray-50 transition-colors"
            >
              <div className="flex items-center justify-between">
                <div className="flex-1 min-w-0">
                  <p className="text-sm text-gray-900 truncate">{item.description}</p>
                  <div className="flex items-center gap-2 mt-1 text-xs text-gray-400">
                    <span>ID: {item.questionId}</span>
                    <span>투표 {item.totalVoteCount}명</span>
                    {item.isExpired && <span className="text-red-400">종료됨</span>}
                  </div>
                </div>
              </div>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
};
